/**
 * Reconciliation Report — finds invoices where total ≠ paid + write_off + credits + balance.
 * Catches sync drift, voided payments, finance edge cases.
 */
import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, Loader2, ScaleIcon, Download, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Wand2, Check, History, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ZohoInvoiceLink, ZohoCrmLeadLink, EnrollmentNoCrmLink, ZohoBooksCustomerButton } from '../../components/ZohoLinks';
import { BookmarkInline, BookmarkMapProvider, useBookmarkMap, bookmarkRowClass } from '../../components/BookmarkButton';

const fmt = (v) => v == null || v === '' || isNaN(Number(v)) ? '-'
  : Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtSigned = (v) => {
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return sign + fmt(Math.abs(n)).replace('-', '');
};

export default function ReconciliationReportPage() {
  return (
    <BookmarkMapProvider>
      <ReconciliationReportContent />
    </BookmarkMapProvider>
  );
}

function ReconciliationReportContent() {
  const { map: bookmarkMap } = useBookmarkMap();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  const [tolerance, setTolerance] = useState('1');
  const [direction, setDirection] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [enrollmentNo, setEnrollmentNo] = useState('');
  const [healingId, setHealingId] = useState(null);   // invoice id currently being healed
  const [healedIds, setHealedIds] = useState({});     // { invoice_id: {ok, still_mismatched, before, after} }
  const [healingBulk, setHealingBulk] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const rowRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize, tolerance });
      if (direction) params.append('direction', direction);
      if (statusFilter) params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (enrollmentNo) params.append('enrollment_no', enrollmentNo);
      const r = await api.get(`/training/reports/reconciliation?${params}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setSummary(r.data.summary || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reconciliation');
    } finally { setLoading(false); }
  }, [page, pageSize, tolerance, direction, statusFilter, dateFrom, dateTo, enrollmentNo]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: ?focus_invoice=<invoice_number> or ?focus_enroll=<enrollment_no>
  const focusInv = searchParams.get('focus_invoice');
  const focusEnroll = searchParams.get('focus_enroll');
  useEffect(() => {
    if (!focusInv && !focusEnroll) return;
    if (loading || rows.length === 0) return;
    const target = rows.find(r =>
      (focusInv && (r.invoice_number === focusInv || r.invoice_id === focusInv)) ||
      (focusEnroll && String(r.enrollment_no) === String(focusEnroll))
    );
    if (!target) {
      toast.info(`${focusInv || focusEnroll} no longer in mismatch list — already healed?`);
      setSearchParams({}, { replace: true });
      return;
    }
    setFlashId(target.invoice_id);
    setTimeout(() => {
      const el = rowRefs.current[target.invoice_id];
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    const t = setTimeout(() => setFlashId(null), 2500);
    setSearchParams({}, { replace: true });
    return () => clearTimeout(t);
  }, [focusInv, focusEnroll, rows, loading, setSearchParams]);

  const healOne = async (invoiceId) => {
    setHealingId(invoiceId);
    try {
      const r = await api.post(`/training/reports/reconciliation/heal/${invoiceId}`);
      setHealedIds(prev => ({ ...prev, [invoiceId]: r.data }));
      if (r.data.ok && !r.data.still_mismatched) {
        toast.success(`Healed ${r.data.invoice_number} — Δ now ₹0`);
      } else if (r.data.ok) {
        toast.warning(`Refreshed ${r.data.invoice_number} but still mismatched (Δ ₹${r.data.after?.new_delta})`);
      } else {
        toast.error(r.data.error || 'Heal failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Heal failed');
    } finally { setHealingId(null); }
  };

  const healAll = async () => {
    if (rows.length === 0) return;
    const batch = rows.slice(0, 50).map(r => r.invoice_id);
    if (!window.confirm(`Re-fetch ${batch.length} invoice${batch.length !== 1 ? 's' : ''} from Zoho? This will refresh invoice details, credit notes, and student aggregates. (~${Math.ceil(batch.length * 0.5)}s)`)) return;
    setHealingBulk(true);
    try {
      const r = await api.post('/training/reports/reconciliation/heal-bulk', { invoice_ids: batch });
      const updates = {};
      (r.data.results || []).forEach(x => { updates[x.invoice_id] = x; });
      setHealedIds(prev => ({ ...prev, ...updates }));
      toast.success(`Healed ${r.data.healed} • Cleared ${r.data.cleared} • Still off: ${r.data.still_mismatched}`);
      // Refresh the list to drop cleared ones
      setTimeout(load, 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk heal failed');
    } finally { setHealingBulk(false); }
  };

  const exportCsv = () => {
    const headers = ['Invoice #', 'Date', 'Status', 'Enrollment', 'Customer', 'Total', 'Paid', 'Write-off', 'Credits Applied', 'Balance', 'Expected', 'Delta', 'Direction'];
    const data = rows.map(r => [
      r.invoice_number, r.date, r.status, r.enrollment_no || '', r.customer_name,
      r.total, r.paid, r.write_off, r.credits_applied, r.balance, r.expected, r.delta, r.direction,
    ]);
    const csv = [headers, ...data].map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `reconciliation-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 space-y-4" data-testid="reconciliation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ScaleIcon className="h-5 w-5 text-amber-600" /> Reconciliation Report
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Invoices where <code className="bg-slate-100 px-1 rounded">total ≠ paid + write_off + credits_applied + balance</code></p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="recon-export"><Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV</Button>
          <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} data-testid="recon-history-open"><History className="h-3.5 w-3.5 mr-1.5" /> Heal History</Button>
          <Button onClick={healAll} disabled={healingBulk || rows.length === 0} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="recon-heal-all">
            {healingBulk ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
            {healingBulk ? 'Healing...' : `Heal All (${Math.min(rows.length, 50)})`}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-5 gap-3">
          <Card className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Scanned</div>
            <div className="text-xl font-semibold text-slate-900 mt-1">{summary.scanned_invoices}</div>
            <div className="text-[10px] text-slate-400 mt-1">invoices</div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-600" /> Mismatched</div>
            <div className="text-xl font-semibold text-amber-700 mt-1">{summary.mismatched}</div>
            <div className="text-[10px] text-slate-400 mt-1">delta &gt; ₹{summary.tolerance}</div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sum of Δ</div>
            <div className={`text-xl font-semibold mt-1 ${summary.mismatched_total_delta < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{fmtSigned(summary.mismatched_total_delta)}</div>
            <div className="text-[10px] text-slate-400 mt-1">total deviation</div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ledger Total</div>
            <div className="text-xl font-semibold text-slate-800 mt-1">{fmt(summary.ledger_total)}</div>
            <div className="text-[10px] text-slate-400 mt-1">all invoices</div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ledger Paid + Credit</div>
            <div className="text-xl font-semibold text-emerald-700 mt-1">{fmt(summary.ledger_paid + summary.ledger_credit)}</div>
            <div className="text-[10px] text-slate-400 mt-1">P {fmt(summary.ledger_paid)} • C {fmt(summary.ledger_credit)}</div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-3">
        <div className="grid grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tolerance (₹)</Label>
            <Input data-testid="recon-tolerance" type="number" value={tolerance} onChange={e => { setTolerance(e.target.value || '1'); setPage(1); }} className="mt-1 h-9 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Direction</Label>
            <Select value={direction || 'both'} onValueChange={v => { setDirection(v === 'both' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="recon-direction" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Both (over &amp; under)</SelectItem>
                <SelectItem value="over">Over (paid &gt; total)</SelectItem>
                <SelectItem value="under">Under (paid &lt; total)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Invoice Status</Label>
            <Select value={statusFilter || 'all'} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="recon-status" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="partially_paid">Partially Paid</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Enrollment No</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input data-testid="recon-enrollment" value={enrollmentNo} onChange={e => { setEnrollmentNo(e.target.value); setPage(1); }} placeholder="e.g. 2602" className="pl-8 h-9 text-xs" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date From</Label>
            <Input data-testid="recon-date-from" type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="mt-1 h-9 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date To</Label>
            <Input data-testid="recon-date-to" type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="mt-1 h-9 text-xs" />
          </div>
        </div>
      </Card>

      {/* Result table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="recon-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase w-6"></TableHead>
                <TableHead className="text-[10px] uppercase">Invoice #</TableHead>
                <TableHead className="text-[10px] uppercase">Date</TableHead>
                <TableHead className="text-[10px] uppercase">Status</TableHead>
                <TableHead className="text-[10px] uppercase">Enrollment</TableHead>
                <TableHead className="text-[10px] uppercase">Customer</TableHead>
                <TableHead className="text-[10px] uppercase text-center">Audit</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Paid</TableHead>
                <TableHead className="text-[10px] uppercase text-right">W/O</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Credit</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Balance</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Expected</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Δ Delta</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Heal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={15} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={15} className="text-center text-slate-400 py-12"><AlertTriangle className="h-6 w-6 text-emerald-500 mx-auto mb-2" /> Books reconciled — no mismatches above tolerance ₹{tolerance}.</TableCell></TableRow>
              ) : rows.map(r => {
                const heal = healedIds[r.invoice_id];
                const isHealing = healingId === r.invoice_id;
                const bmHighlight = r.enrollment_no ? bookmarkRowClass(r.enrollment_no, bookmarkMap) : '';
                const isFlashing = flashId === r.invoice_id;
                const isOpen = expandedId === r.invoice_id;
                return (
                <Fragment key={r.invoice_id}>
                <TableRow
                  ref={el => { if (el) rowRefs.current[r.invoice_id] = el; }}
                  className={`hover:bg-amber-50/30 ${heal && !heal.still_mismatched ? 'bg-emerald-50/50' : ''} ${bmHighlight} ${isFlashing ? 'ring-2 ring-violet-400 ring-offset-1 bg-violet-100/60 transition-all duration-700' : 'transition-all duration-700'}`}
                  data-testid={`recon-row-${r.invoice_id}`}
                >
                  <TableCell className="px-1">
                    <button
                      type="button"
                      onClick={() => setExpandedId(isOpen ? null : r.invoice_id)}
                      className="h-5 w-5 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"
                      data-testid={`recon-expand-${r.invoice_id}`}
                      title={isOpen ? 'Collapse' : 'Expand'}
                    >
                      {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                  </TableCell>
                  <TableCell className="text-xs"><ZohoInvoiceLink invoiceNumber={r.invoice_number} invoiceId={r.invoice_id} className="text-xs" /></TableCell>
                  <TableCell className="text-xs text-slate-600">{r.date}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[9px] ${r.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.status === 'overdue' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{r.status}</Badge></TableCell>
                  <TableCell className="text-xs">{r.enrollment_no ? <EnrollmentNoCrmLink enrollmentNo={r.enrollment_no} className="text-xs" /> : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-xs text-slate-700 truncate max-w-[180px]">{r.customer_name}</TableCell>
                  <TableCell className="text-center">
                    {r.enrollment_no ? (
                      <BookmarkInline student={{
                        enrollment_no: r.enrollment_no,
                        id: r.student_id || r.customer_id || r.enrollment_no,
                        name: r.customer_name || r.student_name || '',
                        course: r.course || r.course_name || '',
                        due_amount: r.balance,
                      }} />
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-right">{fmt(r.total)}</TableCell>
                  <TableCell className="text-xs text-right text-emerald-700">{fmt(heal?.after?.paid ?? r.paid)}</TableCell>
                  <TableCell className="text-xs text-right text-orange-600">{(heal?.after?.write_off ?? r.write_off) > 0 ? fmt(heal?.after?.write_off ?? r.write_off) : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-xs text-right text-violet-700">{(heal?.after?.credits_applied ?? r.credits_applied) > 0 ? fmt(heal?.after?.credits_applied ?? r.credits_applied) : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-xs text-right text-rose-600">{(heal?.after?.balance ?? r.balance) > 0 ? fmt(heal?.after?.balance ?? r.balance) : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-xs text-right text-slate-700">{fmt(r.expected)}</TableCell>
                  <TableCell className={`text-xs text-right font-semibold ${(heal?.after?.new_delta ?? r.delta) === 0 ? 'text-emerald-700' : (heal?.after?.new_delta ?? r.delta) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {heal?.ok ? (
                      heal.still_mismatched ? fmtSigned(heal.after.new_delta) : <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" /> ₹0</span>
                    ) : fmtSigned(r.delta)}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {heal?.ok && !heal.still_mismatched ? (
                      <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">Healed</Badge>
                    ) : (
                      <Button size="sm" variant="ghost" disabled={isHealing}
                        className="h-7 px-2 text-[10px] text-amber-700 hover:bg-amber-50"
                        onClick={() => healOne(r.invoice_id)}
                        data-testid={`recon-heal-${r.invoice_id}`}>
                        {isHealing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Wand2 className="h-3 w-3 mr-1" /> Heal</>}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow className="bg-slate-50/60 hover:bg-slate-50/60" data-testid={`recon-drilldown-${r.invoice_id}`}>
                    <TableCell colSpan={15} className="py-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-2">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Student</p>
                          <div className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs items-center">
                            <span className="text-slate-400">Enrollment:</span>
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium text-slate-700 text-xs">{r.enrollment_no || '-'}</span>
                              {r.enrollment_no && <ZohoCrmLeadLink enrollmentNo={r.enrollment_no} compact />}
                              <ZohoBooksCustomerButton zohoCustomerId={r.customer_id} compact />
                            </span>
                            <span className="text-slate-400">Customer:</span>
                            <span className="text-slate-700 truncate">{r.customer_name || '-'}</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Invoice</p>
                          <div className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs">
                            <span className="text-slate-400">Number:</span>
                            <span><ZohoInvoiceLink invoiceNumber={r.invoice_number} invoiceId={r.invoice_id} className="text-xs" /></span>
                            <span className="text-slate-400">Date:</span><span className="text-slate-700">{r.date}</span>
                            <span className="text-slate-400">Status:</span><span className="capitalize text-slate-700">{r.status}</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Mismatch Breakdown</p>
                          <div className="grid grid-cols-[110px_1fr] gap-y-1 text-xs">
                            <span className="text-slate-400">Total:</span><span className="text-slate-700 text-right">{fmt(r.total)}</span>
                            <span className="text-slate-400">Paid:</span><span className="text-emerald-700 text-right">{fmt(heal?.after?.paid ?? r.paid)}</span>
                            <span className="text-slate-400">Write Off:</span><span className="text-orange-600 text-right">{fmt(heal?.after?.write_off ?? r.write_off)}</span>
                            <span className="text-slate-400">Credits:</span><span className="text-violet-700 text-right">{fmt(heal?.after?.credits_applied ?? r.credits_applied)}</span>
                            <span className="text-slate-400">Balance:</span><span className="text-rose-600 text-right">{fmt(heal?.after?.balance ?? r.balance)}</span>
                            <span className="text-slate-400">Expected:</span><span className="text-slate-700 text-right">{fmt(r.expected)}</span>
                            <span className="text-slate-400 font-semibold">Delta:</span>
                            <span className={`text-right font-semibold ${((heal?.after?.new_delta ?? r.delta) === 0) ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {fmtSigned(heal?.after?.new_delta ?? r.delta)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );})}
            </TableBody>
          </Table>
        </div>
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-slate-600">
            <span>Page {page} of {totalPages} • {total} mismatches</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="recon-prev"><ChevronLeft className="h-3 w-3" /> Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="recon-next">Next <ChevronRight className="h-3 w-3" /></Button>
            </div>
          </div>
        )}
      </Card>

      <HealHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />
    </div>
  );
}

/* =============================================================================
 * HEAL HISTORY DIALOG
 * =============================================================================*/
function HealHistoryDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const r = await api.get(`/training/reports/reconciliation/heal-history?${params}`);
      setRows(r.data.history || []);
      setTotal(r.data.total || 0);
      setSummary(r.data.summary || null);
    } catch (e) { toast.error('Failed to load heal history'); }
    finally { setLoading(false); }
  }, [open, page, pageSize, search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) { setPage(1); setSearch(''); setSearchInput(''); setStatusFilter(''); } }, [open]);

  const exportCsv = () => {
    const headers = ['Healed At', 'Invoice #', 'Enrollment', 'Customer', 'Healed By', 'Before Δ', 'After Δ', 'CNs Refreshed', 'Status'];
    const data = rows.map(r => [
      new Date(r.healed_at).toLocaleString(), r.invoice_number, r.enrollment_no || '',
      r.customer_name || r.student_name, r.healed_by_name,
      r.before_delta, r.resolved_delta, r.credit_notes_refreshed, r.status,
    ]);
    const csv = [headers, ...data].map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `heal-history-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col p-0" data-testid="heal-history-dialog">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-t-lg">
          <DialogTitle className="text-white text-base font-semibold flex items-center gap-2">
            <History className="h-4 w-4" /> Heal History — Audit Trail
          </DialogTitle>
        </DialogHeader>

        {summary && (
          <div className="grid grid-cols-4 gap-3 px-6 pt-4">
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Heal Actions</div><div className="text-xl font-semibold text-slate-900 mt-1">{summary.total_actions}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Cleared</div><div className="text-xl font-semibold text-emerald-700 mt-1">{summary.cleared}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Still Mismatched</div><div className="text-xl font-semibold text-amber-700 mt-1">{summary.still_mismatched}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Δ Recovered</div><div className="text-xl font-semibold text-violet-700 mt-1">{fmt(summary.delta_recovered)}</div></Card>
          </div>
        )}

        <div className="px-6 pt-3 pb-3 flex items-center gap-2 flex-shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input data-testid="hh-search" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
              placeholder="Invoice #, Customer, Enrollment, Healed By..." className="pl-8 h-8 text-xs" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setStatusFilter(statusFilter === 'cleared' ? '' : 'cleared'); setPage(1); }}
            className={statusFilter === 'cleared' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : ''} data-testid="hh-filter-cleared">Cleared</Button>
          <Button variant="outline" size="sm" onClick={() => { setStatusFilter(statusFilter === 'still_mismatched' ? '' : 'still_mismatched'); setPage(1); }}
            className={statusFilter === 'still_mismatched' ? 'bg-amber-50 border-amber-300 text-amber-700' : ''} data-testid="hh-filter-mismatched">Still Mismatched</Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="hh-export"><Download className="h-3.5 w-3.5 mr-1.5" /> CSV</Button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto px-6 pb-3">
          <Table data-testid="hh-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase">Healed At</TableHead>
                <TableHead className="text-[10px] uppercase">Invoice</TableHead>
                <TableHead className="text-[10px] uppercase">Enrollment</TableHead>
                <TableHead className="text-[10px] uppercase">Customer</TableHead>
                <TableHead className="text-[10px] uppercase">Healed By</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Δ Before</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Δ After</TableHead>
                <TableHead className="text-[10px] uppercase text-center">CNs</TableHead>
                <TableHead className="text-[10px] uppercase">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-12">No heal history yet — click any "Heal" button on the report to start</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={r.id || i} className={`hover:bg-amber-50/30 ${r.status === 'cleared' ? '' : 'bg-amber-50/30'}`} data-testid={`hh-row-${r.id}`}>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap">{new Date(r.healed_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs"><ZohoInvoiceLink invoiceNumber={r.invoice_number} invoiceId={r.invoice_id} className="text-xs" /></TableCell>
                  <TableCell className="text-xs">{r.enrollment_no ? <EnrollmentNoCrmLink enrollmentNo={r.enrollment_no} className="text-xs" /> : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-xs text-slate-700 truncate max-w-[180px]">{r.customer_name || r.student_name}</TableCell>
                  <TableCell className="text-xs text-slate-700">{r.healed_by_name}</TableCell>
                  <TableCell className={`text-xs text-right font-medium ${Math.abs(r.before_delta) > 1 ? 'text-rose-700' : 'text-slate-400'}`}>{fmtSigned(r.before_delta)}</TableCell>
                  <TableCell className={`text-xs text-right font-semibold ${Math.abs(r.resolved_delta) <= 1 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {Math.abs(r.resolved_delta) <= 1 ? <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" /> ₹0</span> : fmtSigned(r.resolved_delta)}
                  </TableCell>
                  <TableCell className="text-xs text-center text-violet-700">{r.credit_notes_refreshed || 0}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[9px] ${r.status === 'cleared' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {r.status === 'cleared' ? 'Cleared' : 'Still Mismatched'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {total > pageSize && (
          <div className="flex items-center justify-between px-6 py-3 border-t text-xs text-slate-600">
            <span>Page {page} of {totalPages} • {total} actions</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="hh-prev"><ChevronLeft className="h-3 w-3" /> Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="hh-next">Next <ChevronRight className="h-3 w-3" /></Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
